import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { FriendRequestDto } from 'src/app/data-transfer/entities/friend-request-dto';
import { FriendRequestApiService } from 'src/app/data-transfer/services/friend-request-api-service';
import { DialogActions } from 'src/app/model/dictionary';

@Component({
  selector: 'app-manage-friend-requests-dialog',
  templateUrl: './manage-friend-requests-dialog.component.html',
  styleUrl: './manage-friend-requests-dialog.component.scss',
})
export class ManageFriendRequestsDialogComponent implements OnInit {

  public pendingRequests: FriendRequestDto[] = [];
  public isLoadingPendingInvites = true;
  private processingRequestIds: Set<number> = new Set<number>();
  private selectedRequestIds: Set<number> = new Set<number>();

  constructor(
    private dialogRef: MatDialogRef<ManageFriendRequestsDialogComponent>,
    private friendRequestApiService: FriendRequestApiService
  ) {}

  ngOnInit(): void {
    this.loadPendingInvites();
  }

  public acceptRequest(request: FriendRequestDto): void {
    const requestId = request?.id;

    if (requestId === undefined || requestId === null || this.isRequestProcessing(requestId)) {
      return;
    }

    this.updateProcessingState(requestId, true);

    this.friendRequestApiService.acceptFriendRequest(requestId, true).subscribe({
      next: () => {
        this.removeRequestFromList(requestId);
        this.friendRequestApiService.reloadPendingInvites(true);
      },
      error: () => this.updateProcessingState(requestId, false),
    });
  }

  public rejectRequest(request: FriendRequestDto): void {
    const requestId = request?.id;

    if (requestId === undefined || requestId === null || this.isRequestProcessing(requestId)) {
      return;
    }

    this.updateProcessingState(requestId, true);

    this.friendRequestApiService.rejectFriendRequest(requestId, true).subscribe({
      next: () => {
        this.removeRequestFromList(requestId);
        this.friendRequestApiService.reloadPendingInvites(true);
      },
      error: () => this.updateProcessingState(requestId, false),
    });
  }

  public isRequestProcessing(requestId?: number): boolean {
    if (requestId === undefined || requestId === null) {
      return false;
    }

    return this.processingRequestIds.has(requestId);
  }

  public trackByRequestId(index: number, request: FriendRequestDto): number {
    return request.id;
  }

  public toggleSelection(requestId: number): void {
    const nextState = new Set(this.selectedRequestIds);

    if (nextState.has(requestId)) {
      nextState.delete(requestId);
    } else {
      nextState.add(requestId);
    }

    this.selectedRequestIds = nextState;
  }

  public isSelected(requestId: number): boolean {
    return this.selectedRequestIds.has(requestId);
  }

  public toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedRequestIds = new Set<number>();
    } else {
      this.selectedRequestIds = new Set<number>(this.pendingRequests.map(r => r.id));
    }
  }

  public isAllSelected(): boolean {
    return this.pendingRequests.length > 0 && this.pendingRequests.every(r => this.selectedRequestIds.has(r.id));
  }

  public hasSelectedRequests(): boolean {
    return this.selectedRequestIds.size > 0;
  }

  public acceptSelectedRequests(): void {
    this.processBatchRequests((requestId) =>
      this.friendRequestApiService.acceptFriendRequest(requestId, true)
    );
  }

  public rejectSelectedRequests(): void {
    this.processBatchRequests((requestId) =>
      this.friendRequestApiService.rejectFriendRequest(requestId, true)
    );
  }


  private loadPendingInvites(): void {
    this.isLoadingPendingInvites = true;

    this.friendRequestApiService.getPendingInvites(true).subscribe({
      next: (dtos) => {
        this.pendingRequests = dtos ?? [];

        this.isLoadingPendingInvites = false;
      },
      error: () => {
        this.isLoadingPendingInvites = false;
      },
    });
  }

  private processBatchRequests(apiCall: (requestId: number) => Observable<void>): void {
    const selectedIds = Array.from(this.selectedRequestIds);

    if (selectedIds.length === 0) {
      return;
    }

    let completedCount = 0;
    const totalCount = selectedIds.length;

    selectedIds.forEach(requestId => {
      this.updateProcessingState(requestId, true);

      apiCall(requestId).subscribe({
        next: () => {
          this.removeRequestFromList(requestId);
          completedCount++;

          if (completedCount === totalCount) {
            this.selectedRequestIds = new Set<number>();
            this.friendRequestApiService.reloadPendingInvites(true);
          }
        },
        error: () => {
          this.updateProcessingState(requestId, false);
          completedCount++;

          if (completedCount === totalCount) {
            this.selectedRequestIds = new Set<number>();
            this.friendRequestApiService.reloadPendingInvites(true);
          }
        },
      });
    });
  }

  private removeRequestFromList(requestId: number): void {
    this.pendingRequests = this.pendingRequests.filter((request) => request.id !== requestId);
    this.updateProcessingState(requestId, false);

    if (this.selectedRequestIds.has(requestId)) {
      const nextState = new Set(this.selectedRequestIds);
      nextState.delete(requestId);
      this.selectedRequestIds = nextState;
    }
  }

  private updateProcessingState(requestId: number, isProcessing: boolean): void {
    const nextState = new Set(this.processingRequestIds);

    if (isProcessing) {
      nextState.add(requestId);
    } else {
      nextState.delete(requestId);
    }

    this.processingRequestIds = nextState;
  }

  closeDialog() {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}
